import { useEffect, useState } from "react";
import {
  createManagedUser,
  getManagedUsers,
  setManagedUserPassword,
  updateManagedUserRole,
} from "../../firebase/userManagement";
import { useAuth } from "../../context/AuthContext";
import "./UserManagement.css";

const roles = ["admin", "reviewer", "viewer"];

function UserManagement() {
  const { user: actor } = useAuth();
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "viewer",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [openUserMenuId, setOpenUserMenuId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadUsers = async () => {
    try {
      setUsers(await getManagedUsers());
    } catch {
      setError("Users could not be loaded.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (!openUserMenuId) {
      return undefined;
    }

    const closeUserMenu = (event) => {
      if (!event.target.closest(".user-name-menu")) {
        setOpenUserMenuId(null);
      }
    };

    const closeUserMenuOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpenUserMenuId(null);
      }
    };

    document.addEventListener("mousedown", closeUserMenu);
    document.addEventListener("keydown", closeUserMenuOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeUserMenu);
      document.removeEventListener("keydown", closeUserMenuOnEscape);
    };
  }, [openUserMenuId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setIsSubmitting(true);

    try {
      await createManagedUser(formData, actor);
      setFormData({ email: "", password: "", role: "viewer" });
      setSuccess("User created successfully.");
      await loadUsers();
    } catch (creationError) {
      const messages = {
        "auth/email-already-in-use": "That email address is already in use.",
        "auth/weak-password": "Password must be at least 6 characters.",
        "permission-denied": "Firestore denied this request. Deploy the updated firestore.rules file first.",
      };
      const message = messages[creationError?.code]
        || "The user could not be created. Check the browser console for the Firebase error.";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoleChange = async (selectedUser, role) => {
    setError("");
    setSuccess("");

    try {
      await updateManagedUserRole(selectedUser, role, actor);
      setUsers((current) => current.map((managedUser) =>
        managedUser.id === selectedUser.id
          ? { ...managedUser, role }
          : managedUser
      ));
      setSuccess("Role updated successfully.");
    } catch {
      setError("The role could not be updated.");
    }
  };

  const handlePasswordChange = async (selectedUser) => {
    setOpenUserMenuId(null);
    setError("");
    setSuccess("");

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setPasswordUserId(selectedUser.id);

    try {
      await setManagedUserPassword(selectedUser, newPassword);
      setNewPassword("");
      setSuccess(`Password updated for ${selectedUser.email}.`);
    } catch (resetError) {
      setError(
        resetError?.code === "functions/permission-denied"
          ? "Only administrators can change user passwords."
          : resetError?.code === "functions/invalid-argument"
            ? "Password must be at least 6 characters."
            : "The password could not be updated. Deploy the Firebase Function first."
      );
    } finally {
      setPasswordUserId(null);
    }
  };

  const userKpis = [
    { label: "TOTAL USERS", value: users.length },
    { label: "ADMINS", value: users.filter((user) => user.role === "admin").length },
    { label: "REVIEWERS", value: users.filter((user) => user.role === "reviewer").length },
    { label: "VIEWERS", value: users.filter((user) => user.role === "viewer").length },
  ];

  return (
    <main className="user-management-page">
      <header className="user-management-header">
        <span>ADMINISTRATION</span>
        <h1>User Management</h1>
        <p>Create workspace accounts and assign their access level.</p>
      </header>

      <section className="user-kpis" aria-label="User account summary">
        {userKpis.map((kpi) => (
          <div className="user-kpi" key={kpi.label}>
            <span>{kpi.label}</span>
            <strong>{kpi.value}</strong>
          </div>
        ))}
      </section>

      <section className="user-management-grid">
        <form className="user-panel user-form" onSubmit={handleSubmit}>
          <div className="user-panel-heading">
            <span>NEW ACCOUNT</span>
            <h2>Add User</h2>
          </div>

          <label htmlFor="user-email">Email address</label>
          <input
            id="user-email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="name@company.com"
            required
          />

          <label htmlFor="user-password">Temporary password</label>
          <input
            id="user-password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            minLength="6"
            placeholder="At least 6 characters"
            required
          />

          <label htmlFor="user-role">Role</label>
          <select id="user-role" name="role" value={formData.role} onChange={handleChange}>
            {roles.map((role) => <option key={role} value={role}>{role}</option>)}
          </select>

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create User"}
          </button>
        </form>

        <section className="user-panel user-list-panel">
          <div className="user-panel-heading">
            <span>WORKSPACE ACCESS</span>
            <h2>Existing Users</h2>
          </div>

          {isLoading && <p className="user-state">Loading users...</p>}
          {!isLoading && users.length === 0 && <p className="user-state">No users found.</p>}
          {!isLoading && users.length > 0 && (
            <div className="user-table-wrapper">
              <table className="user-table">
                <thead>
                  <tr><th>USER</th><th>ROLE</th></tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-name-menu">
                          <button
                            type="button"
                            className="user-name-button"
                            onClick={() => setOpenUserMenuId((current) =>
                              current === user.id ? null : user.id
                            )}
                            aria-expanded={openUserMenuId === user.id}
                          >
                            {user.displayName || user.email || "Unnamed user"}
                          </button>
                          {openUserMenuId === user.id && (
                            <div className="user-action-menu">
                              <label htmlFor={`password-${user.id}`}>New password</label>
                              <input
                                id={`password-${user.id}`}
                                type="password"
                                value={passwordUserId === user.id ? newPassword : ""}
                                onChange={(event) => {
                                  setPasswordUserId(user.id);
                                  setNewPassword(event.target.value);
                                }}
                                minLength="6"
                                placeholder="At least 6 characters"
                              />
                              <button
                                type="button"
                                onClick={() => handlePasswordChange(user)}
                                disabled={passwordUserId === user.id && !newPassword}
                              >
                                {passwordUserId === user.id && newPassword ? "Change password" : "Enter password"}
                              </button>
                            </div>
                          )}
                        </div>
                        <small>{user.email}</small>
                      </td>
                      <td>
                        <select
                          value={user.role || "viewer"}
                          onChange={(event) => handleRoleChange(user, event.target.value)}
                        >
                          {roles.map((role) => <option key={role} value={role}>{role}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </section>

      {error && <p className="user-message user-error" role="alert">{error}</p>}
      {success && <p className="user-message user-success" role="status">{success}</p>}
    </main>
  );
}

export default UserManagement;
